import { Link } from "react-router-dom";
import { FaExternalLinkAlt, FaSyncAlt, FaWrench } from "react-icons/fa";
import { formatTimestamp } from "src/utils/dateHandler.util";
import { NotificationHealth } from "../types/notification-health.entity";
import { NotificationHealthIssueBadge } from "./notification-health-issue-badge";

type Props = {
  data: (NotificationHealth & { id: string })[];
  onRecheck: (userId: string) => void;
  onRepair: (userId: string) => void;
  activeAction: { userId: string; type: "recheck" | "repair" } | null;
};

export const NotificationHealthTable = ({ data, onRecheck, onRepair, activeAction }: Props) => (
  <div className="mb-6 overflow-x-auto border border-slate-200 bg-white shadow-sm">
    <table className="min-w-[980px] w-full divide-y divide-slate-200 text-left text-xs">
      <thead className="bg-slate-900 text-[10px] uppercase tracking-wide text-white">
        <tr>
          <th className="px-4 py-3">User</th>
          <th className="px-4 py-3">Current exceptions</th>
          <th className="px-4 py-3">Tokens</th>
          <th className="px-4 py-3">Routing topics</th>
          <th className="px-4 py-3">First detected</th>
          <th className="px-4 py-3">Last checked</th>
          <th className="px-4 py-3 text-center">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {data.length === 0 ? (
          <tr>
            <td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-500">
              No notification-routing exceptions found.
            </td>
          </tr>
        ) : (
          data.map((health) => (
            <tr key={health.id} className="odd:bg-white even:bg-slate-50 hover:bg-amber-50/40">
              <td className="px-4 py-3">
                <Link
                  to={`/users/${health.userId}`}
                  className="inline-flex max-w-[190px] rounded border border-sky-200 bg-sky-50 px-2 py-1 font-mono text-[10px] font-semibold text-sky-700 no-underline transition-colors hover:bg-sky-100"
                  title="Open user"
                >
                  <span className="truncate">{health.userId}</span>
                </Link>
              </td>
              <td className="px-4 py-3">
                <div className="flex min-w-[250px] flex-wrap gap-1.5">
                  {health.issueCodes.map((issueCode) => (
                    <NotificationHealthIssueBadge
                      key={issueCode}
                      issueCode={issueCode}
                    />
                  ))}
                </div>
              </td>
              <td className="px-4 py-3 font-semibold text-slate-800">{health.tokenCount}</td>
              <td className="px-4 py-3">
                <div className="grid min-w-[130px] gap-1 text-[10px]">
                  <div className="flex items-center justify-between gap-3 text-slate-600">
                    <span>Stored</span>
                    <span className="font-semibold text-slate-800">{health.storedTopicCount}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 text-slate-600">
                    <span>Recorded FCM</span>
                    <span className="font-semibold text-slate-800">{health.recordedFcmTopicCount}</span>
                  </div>
                  <div className="border-t border-slate-200 pt-1 text-slate-500">
                    Expected stored: {health.expectedStoredTopicCount}
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 text-slate-700">{formatTimestamp(health.firstDetectedAt)}</td>
              <td className="px-4 py-3 text-slate-700">{formatTimestamp(health.checkedAt)}</td>
              <td className="px-4 py-3 text-center">
                <div className="flex justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => onRecheck(health.userId)}
                    disabled={activeAction !== null}
                    title="Recheck notification routing"
                    aria-label={`Recheck notification routing for ${health.userId}`}
                    className="inline-flex h-9 w-9 items-center justify-center border border-emerald-200 bg-emerald-50 text-emerald-700 transition-colors hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <FaSyncAlt className={activeAction?.userId === health.userId && activeAction.type === "recheck" ? "animate-spin" : ""} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onRepair(health.userId)}
                    disabled={activeAction !== null}
                    title="Repair notification routing"
                    aria-label={`Repair notification routing for ${health.userId}`}
                    className="inline-flex h-9 w-9 items-center justify-center border border-amber-200 bg-amber-50 text-amber-800 transition-colors hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <FaWrench className={activeAction?.userId === health.userId && activeAction.type === "repair" ? "animate-pulse" : ""} />
                  </button>
                  <Link
                    to={`/users/${health.userId}#notifications`}
                    title="Open user diagnostics"
                    aria-label={`Open notification diagnostics for ${health.userId}`}
                    className="inline-flex h-9 w-9 items-center justify-center bg-sky-600 text-white transition-colors hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2"
                  >
                    <FaExternalLinkAlt />
                  </Link>
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);
